<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import TeamsAPI from 'dashboard/api/teams';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import KanbanColumn from '../components/KanbanColumn.vue';
import CardFormModal from '../components/CardFormModal.vue';
import CrmReportModal from '../components/CrmReportModal.vue';

const { t } = useI18n();
const route = useRoute();
const store = useStore();
const accountTeams = ref([]);
const selectedFunnelId = ref(null);
const statusFilter = ref('all');
const searchQuery = ref('');
const showAgentFilter = ref(false);
const selectedAgentIds = ref([]);
const showTeamFilter = ref(false);
const selectedTeamIds = ref([]);
const showCardModal = ref(false);
const editingCard = ref(null);
const addingToStageId = ref(null);
const showDeleteConfirm = ref(false);
const deletingCard = ref(null);
const showLossModal = ref(false);
const lossReasonCard = ref(null);
const lossReasonText = ref('');
const showReportModal = ref(false);

// Drag-to-scroll for Kanban board
const boardRef = ref(null);
const isDraggingBoard = ref(false);
const dragStartX = ref(0);
const scrollStartX = ref(0);

const onBoardMouseDown = e => {
  if (e.target.closest('[draggable="true"]') || e.target.closest('button'))
    return;
  isDraggingBoard.value = true;
  dragStartX.value = e.clientX;
  scrollStartX.value = boardRef.value.scrollLeft;
  boardRef.value.style.cursor = 'grabbing';
  boardRef.value.style.userSelect = 'none';
};

const onBoardMouseMove = e => {
  if (!isDraggingBoard.value) return;
  const dx = e.clientX - dragStartX.value;
  boardRef.value.scrollLeft = scrollStartX.value - dx;
};

const onBoardMouseUp = () => {
  if (!isDraggingBoard.value) return;
  isDraggingBoard.value = false;
  boardRef.value.style.cursor = '';
  boardRef.value.style.userSelect = '';
};

// eslint-disable-next-line no-use-before-define
onBeforeUnmount(() => {
  document.removeEventListener('mousemove', onBoardMouseMove);
  document.removeEventListener('mouseup', onBoardMouseUp);
  // eslint-disable-next-line no-use-before-define
  document.removeEventListener('mousedown', onClickOutsideAgentFilter);
});

const funnels = useMapGetter('funnels/getFunnels');
const currentFunnel = useMapGetter('funnels/getCurrentFunnel');
const cards = useMapGetter('funnels/getCards');
const uiFlags = useMapGetter('funnels/getUIFlags');
const allTeams = computed(() => accountTeams.value);

// Agent filter logic
const agentFilterRef = ref(null);
const teamFilterRef = ref(null);

const assigneesInCards = computed(() => {
  const map = new Map();
  cards.value.forEach(c => {
    if (c.assignee) map.set(c.assignee.id, c.assignee);
  });
  return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
});

const allAgentsSelected = computed(() => {
  if (selectedAgentIds.value.length === 0) return true;
  return selectedAgentIds.value.length === assigneesInCards.value.length;
});

const toggleAgent = id => {
  // If currently "all selected" (empty array), populate with all then toggle
  if (selectedAgentIds.value.length === 0) {
    selectedAgentIds.value = assigneesInCards.value
      .map(a => a.id)
      .filter(aid => aid !== id);
    return;
  }
  const idx = selectedAgentIds.value.indexOf(id);
  if (idx >= 0) {
    selectedAgentIds.value.splice(idx, 1);
  } else {
    selectedAgentIds.value.push(id);
  }
};

const toggleAllAgents = () => {
  selectedAgentIds.value = [];
};

const agentFilterLabel = computed(() => {
  if (selectedAgentIds.value.length === 0 || allAgentsSelected.value) {
    return t('CRM.AGENT_FILTER_ALL');
  }
  if (selectedAgentIds.value.length === 1) {
    const agent = assigneesInCards.value.find(
      a => a.id === selectedAgentIds.value[0]
    );
    return agent?.name?.split(' ')[0] || t('CRM.AGENT_FILTER_ALL');
  }
  return t('CRM.AGENT_FILTER_COUNT', { count: selectedAgentIds.value.length });
});

const onClickOutsideAgentFilter = e => {
  if (agentFilterRef.value && !agentFilterRef.value.contains(e.target)) {
    showAgentFilter.value = false;
  }
  if (teamFilterRef.value && !teamFilterRef.value.contains(e.target)) {
    showTeamFilter.value = false;
  }
};

// Team filter logic — lista todos os times da conta (inclusive para
// agentes restritos, que podem filtrar pelos times dos quais são membros)
const teamsForFilter = computed(() => {
  return [...(allTeams.value || [])].sort((a, b) =>
    a.name.localeCompare(b.name)
  );
});

const allTeamsSelected = computed(() => {
  return (
    teamsForFilter.value.length > 0 &&
    selectedTeamIds.value.length === teamsForFilter.value.length
  );
});

const toggleTeam = id => {
  const idx = selectedTeamIds.value.indexOf(id);
  if (idx >= 0) {
    selectedTeamIds.value.splice(idx, 1);
  } else {
    selectedTeamIds.value.push(id);
  }
};

const toggleAllTeams = () => {
  if (allTeamsSelected.value) {
    selectedTeamIds.value = [];
  } else {
    selectedTeamIds.value = teamsForFilter.value.map(tm => tm.id);
  }
};

const teamFilterLabel = computed(() => {
  if (selectedTeamIds.value.length === 0) {
    return t('CRM.TEAM_FILTER_ALL');
  }
  if (selectedTeamIds.value.length === 1) {
    const team = teamsForFilter.value.find(
      tm => tm.id === selectedTeamIds.value[0]
    );
    return team?.name || t('CRM.TEAM_FILTER_ALL');
  }
  return t('CRM.TEAM_FILTER_COUNT', { count: selectedTeamIds.value.length });
});

const stages = computed(() => currentFunnel.value?.funnel_stages || []);

// Status counts
const statusCounts = computed(() => {
  const all = cards.value;
  return {
    all: all.length,
    open: all.filter(c => c.status === 'open').length,
    won: all.filter(c => c.status === 'won').length,
    lost: all.filter(c => c.status === 'lost').length,
  };
});

// Todos os cards que passam pelos filtros ativos (status, busca, agente, time),
// independente de etapa. O Kanban recorta por etapa e o relatório reusa a lista
// inteira — assim os dois nunca divergem.
const filteredCards = computed(() => {
  const query = searchQuery.value.toLowerCase().trim();
  const teamIds = selectedTeamIds.value;
  const teamFilterActive = teamIds.length > 0;
  const agentIds = selectedAgentIds.value;
  // Team filter overrides agent filter — when a team is selected we show
  // every card whose contact belongs to that team, regardless of assignee.
  const filterByAgent =
    !teamFilterActive && agentIds.length > 0 && !allAgentsSelected.value;
  return cards.value
    .filter(
      c => statusFilter.value === 'all' || c.status === statusFilter.value
    )
    .filter(c => {
      if (!query) return true;
      return (
        c.title?.toLowerCase().includes(query) ||
        c.contact?.name?.toLowerCase().includes(query)
      );
    })
    .filter(c => {
      if (!filterByAgent) return true;
      return c.assignee && agentIds.includes(c.assignee.id);
    })
    .filter(c => {
      if (!teamFilterActive) return true;
      const cTeamIds = c.contact?.team_ids || [];
      return cTeamIds.some(tid => teamIds.includes(tid));
    });
});

const getCardsByStage = stageId =>
  filteredCards.value
    .filter(c => c.funnel_stage_id === stageId)
    .sort((a, b) => a.position - b.position);

// Funnel first stage color for pills
const funnelDotColor = funnel => {
  const firstStage = funnel.funnel_stages?.[0];
  return firstStage?.color || '#6B7280';
};

onMounted(async () => {
  document.addEventListener('mousemove', onBoardMouseMove);
  document.addEventListener('mouseup', onBoardMouseUp);
  document.addEventListener('mousedown', onClickOutsideAgentFilter);
  await store.dispatch('funnels/get');
  if (funnels.value.length > 0) {
    const requestedId = Number(route.query.funnel_id);
    const requested = funnels.value.find(f => f.id === requestedId);
    selectedFunnelId.value = requested ? requested.id : funnels.value[0].id;
  }
  try {
    const response = await TeamsAPI.get(false);
    const data = response?.data;
    accountTeams.value = Array.isArray(data) ? data : data?.payload || [];
  } catch {
    accountTeams.value = [];
  }
});

watch(selectedFunnelId, async id => {
  if (id) {
    await store.dispatch('funnels/show', id);
    await store.dispatch('funnels/getCards', id);
  }
});

const onAddCard = stage => {
  editingCard.value = null;
  addingToStageId.value = stage.id;
  showCardModal.value = true;
};

const onEditCard = card => {
  editingCard.value = card;
  addingToStageId.value = null;
  showCardModal.value = true;
};

const onDeleteCard = card => {
  deletingCard.value = card;
  showDeleteConfirm.value = true;
};

const confirmDeleteCard = async () => {
  try {
    await store.dispatch('funnels/deleteCard', {
      funnelId: selectedFunnelId.value,
      cardId: deletingCard.value.id,
    });
    useAlert(t('CRM.CARD_DELETED'));
  } catch {
    useAlert(t('CRM.CARD_DELETE_ERROR'));
  }
  showDeleteConfirm.value = false;
  deletingCard.value = null;
};

const onSaveCard = async ({ id, funnel_card: cardData }) => {
  try {
    if (id) {
      await store.dispatch('funnels/updateCard', {
        funnelId: selectedFunnelId.value,
        cardId: id,
        funnel_card: cardData,
      });
      useAlert(t('CRM.CARD_UPDATED'));
    } else {
      await store.dispatch('funnels/createCard', {
        funnelId: selectedFunnelId.value,
        funnel_card: cardData,
      });
      useAlert(t('CRM.CARD_CREATED'));
    }
    showCardModal.value = false;
  } catch {
    useAlert(t('CRM.CARD_SAVE_ERROR'));
  }
};

const onDropCard = async ({ cardId, stageId, position }) => {
  try {
    await store.dispatch('funnels/moveCard', {
      funnelId: selectedFunnelId.value,
      cardId,
      funnelStageId: stageId,
      position,
    });
  } catch {
    useAlert(t('CRM.CARD_MOVE_ERROR'));
  }
};

// Quick actions from cards
const onMarkWon = async card => {
  try {
    await store.dispatch('funnels/markCardWon', {
      funnelId: selectedFunnelId.value,
      cardId: card.id,
    });
    useAlert(t('CRM.DETAIL.WON_SUCCESS'));
  } catch {
    useAlert(t('CRM.DETAIL.STATUS_ERROR'));
  }
};

const onMarkLostRequest = card => {
  lossReasonCard.value = card;
  lossReasonText.value = '';
  showLossModal.value = true;
};

const confirmMarkLost = async () => {
  try {
    await store.dispatch('funnels/markCardLost', {
      funnelId: selectedFunnelId.value,
      cardId: lossReasonCard.value.id,
      lossReason: lossReasonText.value,
    });
    useAlert(t('CRM.DETAIL.LOST_SUCCESS'));
  } catch {
    useAlert(t('CRM.DETAIL.STATUS_ERROR'));
  }
  showLossModal.value = false;
  lossReasonCard.value = null;
  lossReasonText.value = '';
};

const statusTabs = computed(() => [
  { value: 'all', label: t('CRM.STATUS_ALL'), count: statusCounts.value.all },
  {
    value: 'open',
    label: t('CRM.STATUS_OPEN_TAB'),
    count: statusCounts.value.open,
  },
  {
    value: 'won',
    label: t('CRM.STATUS_WON_TAB'),
    count: statusCounts.value.won,
  },
  {
    value: 'lost',
    label: t('CRM.STATUS_LOST_TAB'),
    count: statusCounts.value.lost,
  },
]);

// Descreve no impresso quais filtros estavam ativos, para o relatório
// não ser confundido com a lista completa do funil.
const reportFilterSummary = computed(() => {
  const parts = [];
  const statusTab = statusTabs.value.find(
    tb => tb.value === statusFilter.value
  );
  if (statusFilter.value !== 'all' && statusTab) {
    parts.push(`${t('CRM.DETAIL.STATUS')}: ${statusTab.label}`);
  }
  if (searchQuery.value.trim()) {
    parts.push(
      `${t('CRM.REPORT.FILTER_SEARCH')}: "${searchQuery.value.trim()}"`
    );
  }
  if (selectedAgentIds.value.length > 0 && !allAgentsSelected.value) {
    parts.push(`${t('CRM.REPORT.FILTER_AGENT')}: ${agentFilterLabel.value}`);
  }
  if (selectedTeamIds.value.length > 0) {
    parts.push(`${t('CRM.REPORT.FILTER_TEAM')}: ${teamFilterLabel.value}`);
  }
  return parts.length ? parts.join(' · ') : t('CRM.REPORT.FILTER_NONE');
});
</script>

<template>
  <div class="flex flex-col flex-1 h-full overflow-hidden bg-n-background">
    <!-- Header -->
    <header
      class="flex flex-col gap-3 px-4 lg:px-6 py-4 border-b border-n-weak"
    >
      <!-- Row 1: Title + Status Tabs -->
      <div class="flex items-center justify-between gap-4">
        <h1 class="text-xl lg:text-2xl font-bold text-n-slate-12 flex-shrink-0">
          {{ t('CRM.TITLE') }}
        </h1>

        <!-- Status filter tabs (desktop) -->
        <div
          v-if="cards.length > 0"
          class="hidden lg:flex bg-n-alpha-2 rounded-lg p-0.5"
        >
          <button
            v-for="tab in statusTabs"
            :key="tab.value"
            class="px-3 py-1 text-sm rounded-md transition-all duration-150"
            :class="
              statusFilter === tab.value
                ? 'bg-white dark:bg-n-slate-3 shadow-sm font-medium text-n-slate-12'
                : 'text-n-slate-10 hover:text-n-slate-12'
            "
            @click="statusFilter = tab.value"
          >
            {{ tab.label }}
            <span class="ml-1 text-[11px] opacity-60">{{ tab.count }}</span>
          </button>
        </div>

        <Button
          v-if="currentFunnel"
          type="button"
          faded
          slate
          size="sm"
          icon="i-lucide-printer"
          class="flex-shrink-0"
          :label="t('CRM.REPORT.BUTTON')"
          @click="showReportModal = true"
        />
      </div>

      <!-- Row 2: Search + Agent filter -->
      <div class="flex items-center gap-3">
        <input
          v-model="searchQuery"
          type="text"
          :placeholder="t('CRM.SEARCH_PLACEHOLDER')"
          class="flex-1 min-w-0 px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand transition-all"
        />

        <!-- Agent filter dropdown -->
        <div
          v-if="assigneesInCards.length > 1"
          ref="agentFilterRef"
          class="relative flex-shrink-0"
        >
          <button
            class="flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 hover:bg-n-alpha-2 transition-all"
            @click="showAgentFilter = !showAgentFilter"
          >
            <span class="i-lucide-users size-4 text-n-slate-9" />
            <span class="hidden sm:inline max-w-[120px] truncate">{{
              agentFilterLabel
            }}</span>
            <span
              class="i-lucide-chevron-down size-3.5 text-n-slate-9 transition-transform"
              :class="{ 'rotate-180': showAgentFilter }"
            />
          </button>
          <div
            v-if="showAgentFilter"
            class="absolute right-0 top-full mt-1 w-64 bg-white dark:bg-n-slate-3 rounded-lg shadow-lg border border-n-weak z-50 py-1 max-h-72 overflow-y-auto"
          >
            <!-- Select all -->
            <label
              class="flex items-center gap-3 px-3 py-2 hover:bg-n-alpha-2 cursor-pointer border-b border-n-weak"
            >
              <input
                type="checkbox"
                :checked="allAgentsSelected"
                class="size-4 rounded border-n-weak text-n-brand accent-n-brand"
                @change="toggleAllAgents"
              />
              <span class="text-sm font-medium text-n-slate-12">
                {{ t('CRM.AGENT_FILTER_SELECT_ALL') }}
              </span>
            </label>
            <!-- Agent list -->
            <label
              v-for="agent in assigneesInCards"
              :key="agent.id"
              class="flex items-center gap-3 px-3 py-2 hover:bg-n-alpha-2 cursor-pointer"
            >
              <input
                type="checkbox"
                :checked="
                  selectedAgentIds.includes(agent.id) || allAgentsSelected
                "
                class="size-4 rounded border-n-weak text-n-brand accent-n-brand"
                @change="toggleAgent(agent.id)"
              />
              <Avatar
                :name="agent.name"
                :src="agent.thumbnail"
                :size="24"
                rounded-full
              />
              <span class="text-sm text-n-slate-12 truncate">
                {{ agent.name }}
              </span>
            </label>
          </div>
        </div>

        <!-- Team filter dropdown -->
        <div
          v-if="teamsForFilter.length > 0"
          ref="teamFilterRef"
          class="relative flex-shrink-0"
        >
          <button
            class="flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 hover:bg-n-alpha-2 transition-all"
            @click="showTeamFilter = !showTeamFilter"
          >
            <span class="i-lucide-users-round size-4 text-n-slate-9" />
            <span class="hidden sm:inline max-w-[120px] truncate">{{
              teamFilterLabel
            }}</span>
            <span
              class="i-lucide-chevron-down size-3.5 text-n-slate-9 transition-transform"
              :class="{ 'rotate-180': showTeamFilter }"
            />
          </button>
          <div
            v-if="showTeamFilter"
            class="absolute right-0 top-full mt-1 w-64 bg-white dark:bg-n-slate-3 rounded-lg shadow-lg border border-n-weak z-50 py-1 max-h-72 overflow-y-auto"
          >
            <label
              class="flex items-center gap-3 px-3 py-2 hover:bg-n-alpha-2 cursor-pointer border-b border-n-weak"
            >
              <input
                type="checkbox"
                :checked="allTeamsSelected"
                class="size-4 rounded border-n-weak text-n-brand accent-n-brand"
                @change="toggleAllTeams"
              />
              <span class="text-sm font-medium text-n-slate-12">
                {{ t('CRM.TEAM_FILTER_SELECT_ALL') }}
              </span>
            </label>
            <label
              v-for="team in teamsForFilter"
              :key="team.id"
              class="flex items-center gap-3 px-3 py-2 hover:bg-n-alpha-2 cursor-pointer"
            >
              <input
                type="checkbox"
                :checked="selectedTeamIds.includes(team.id)"
                class="size-4 rounded border-n-weak text-n-brand accent-n-brand"
                @change="toggleTeam(team.id)"
              />
              <span class="i-lucide-users-round size-5 text-n-slate-9" />
              <span class="text-sm text-n-slate-12 truncate">
                {{ team.name }}
              </span>
            </label>
          </div>
        </div>
      </div>

      <!-- Row 3: Status tabs (mobile) -->
      <div
        v-if="cards.length > 0"
        class="flex lg:hidden bg-n-alpha-2 rounded-lg p-0.5 overflow-x-auto"
      >
        <button
          v-for="tab in statusTabs"
          :key="tab.value"
          class="flex-1 px-2 py-1 text-sm rounded-md transition-all duration-150 whitespace-nowrap"
          :class="
            statusFilter === tab.value
              ? 'bg-white dark:bg-n-slate-3 shadow-sm font-medium text-n-slate-12'
              : 'text-n-slate-10 hover:text-n-slate-12'
          "
          @click="statusFilter = tab.value"
        >
          {{ tab.label }}
          <span class="ml-1 text-[11px] opacity-60">{{ tab.count }}</span>
        </button>
      </div>

      <!-- Funnel selector pills -->
      <div
        v-if="funnels.length > 0"
        class="flex gap-1.5 overflow-x-auto pb-0.5 -mx-1 px-1"
      >
        <button
          v-for="funnel in funnels"
          :key="funnel.id"
          class="flex items-center gap-2 px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-all duration-150"
          :class="
            selectedFunnelId === funnel.id
              ? 'bg-white dark:bg-n-slate-3 shadow-sm font-medium text-n-slate-12'
              : 'text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2'
          "
          @click="selectedFunnelId = funnel.id"
        >
          <span
            class="size-2 rounded-full flex-shrink-0"
            :style="{ backgroundColor: funnelDotColor(funnel) }"
          />
          {{ funnel.name }}
        </button>
      </div>
    </header>

    <!-- Loading skeleton -->
    <div
      v-if="uiFlags.isFetching || uiFlags.isFetchingCards"
      class="flex gap-4 flex-1 overflow-hidden p-4"
    >
      <div
        v-for="i in 4"
        :key="i"
        class="flex flex-col flex-shrink-0 w-[300px] rounded-xl overflow-hidden"
      >
        <div class="h-11 bg-n-slate-3 animate-pulse rounded-t-xl" />
        <div
          class="flex-1 bg-n-slate-2 dark:bg-n-slate-3 rounded-b-xl p-2 flex flex-col gap-2"
        >
          <div
            v-for="j in 3"
            :key="j"
            class="h-24 bg-n-slate-3 dark:bg-n-slate-4 animate-pulse rounded-lg"
          />
        </div>
      </div>
    </div>

    <!-- Empty: no funnels -->
    <div
      v-else-if="funnels.length === 0"
      class="flex flex-col items-center justify-center flex-1 gap-3"
    >
      <div class="size-16 rounded-full bg-n-alpha-2 grid place-content-center">
        <span class="i-lucide-kanban size-8 text-n-slate-9" />
      </div>
      <p class="text-base font-medium text-n-slate-11">
        {{ t('CRM.NO_FUNNELS') }}
      </p>
      <p class="text-sm text-n-slate-10">{{ t('CRM.NO_FUNNELS_DESC') }}</p>
    </div>

    <!-- Empty: no stages -->
    <div
      v-else-if="stages.length === 0"
      class="flex flex-col items-center justify-center flex-1 gap-3"
    >
      <div class="size-16 rounded-full bg-n-alpha-2 grid place-content-center">
        <span class="i-lucide-columns-3 size-8 text-n-slate-9" />
      </div>
      <p class="text-base font-medium text-n-slate-11">
        {{ t('CRM.NO_STAGES') }}
      </p>
      <p class="text-sm text-n-slate-10">{{ t('CRM.NO_STAGES_DESC') }}</p>
    </div>

    <!-- Kanban board -->
    <div
      v-else
      ref="boardRef"
      class="flex gap-4 flex-1 overflow-x-auto p-4 cursor-grab"
      @mousedown="onBoardMouseDown"
    >
      <KanbanColumn
        v-for="stage in stages"
        :key="stage.id"
        :stage="stage"
        :cards="getCardsByStage(stage.id)"
        :funnel-id="selectedFunnelId"
        @add-card="onAddCard"
        @edit-card="onEditCard"
        @delete-card="onDeleteCard"
        @drop-card="onDropCard"
        @won-card="onMarkWon"
        @lost-card="onMarkLostRequest"
      />
    </div>

    <!-- Card form modal -->
    <woot-modal
      v-model:show="showCardModal"
      :on-close="() => (showCardModal = false)"
    >
      <CardFormModal
        v-if="showCardModal"
        :card="editingCard"
        :stage-id="addingToStageId"
        @close="showCardModal = false"
        @save="onSaveCard"
      />
    </woot-modal>

    <!-- Report modal -->
    <woot-modal
      v-model:show="showReportModal"
      :on-close="() => (showReportModal = false)"
    >
      <CrmReportModal
        v-if="showReportModal"
        :funnel-name="currentFunnel?.name || ''"
        :stages="stages"
        :cards="filteredCards"
        :filter-summary="reportFilterSummary"
        @close="showReportModal = false"
      />
    </woot-modal>

    <!-- Delete confirmation -->
    <woot-delete-modal
      v-model:show="showDeleteConfirm"
      :on-close="() => (showDeleteConfirm = false)"
      :on-confirm="confirmDeleteCard"
      :title="t('CRM.DELETE_CARD_TITLE')"
      :message="t('CRM.DELETE_CARD_MESSAGE')"
      :confirm-text="t('CRM.DELETE_CARD_CONFIRM')"
      :reject-text="t('CRM.DELETE_CARD_CANCEL')"
    />

    <!-- Loss reason modal -->
    <woot-modal
      v-model:show="showLossModal"
      :on-close="() => (showLossModal = false)"
    >
      <div class="flex flex-col h-auto overflow-auto">
        <woot-modal-header :header-title="t('CRM.DETAIL.LOSS_REASON_TITLE')" />
        <form class="w-full" @submit.prevent="confirmMarkLost">
          <div class="w-full">
            <label>
              {{ t('CRM.DETAIL.LOSS_REASON_TITLE') }}
              <textarea
                v-model="lossReasonText"
                rows="3"
                :placeholder="t('CRM.DETAIL.LOSS_REASON_PLACEHOLDER')"
              />
            </label>
          </div>
          <div class="flex justify-end gap-2 py-2">
            <Button
              faded
              slate
              type="button"
              :label="t('CRM.DETAIL.LOSS_REASON_CANCEL')"
              @click="showLossModal = false"
            />
            <Button
              type="submit"
              :label="t('CRM.DETAIL.LOSS_REASON_CONFIRM')"
              class="bg-n-ruby-9 hover:bg-n-ruby-10 text-white"
            />
          </div>
        </form>
      </div>
    </woot-modal>
  </div>
</template>
